import React, { useContext, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Form from "react-bootstrap/Form";
import { AppContext } from "../context/AppContext";
import {
  getWithAuthCallWithErrorResponse,
  // NOTE: use PostCallWithErrorResponse, NOT postWithAuthCallWithErrorResponse.
  // That helper sends the raw token with no "Bearer " prefix (and has its
  // Content-Type commented out), but the backend does
  // `authHeader.split(' ')[1]` — so every call with it fails with
  // "The security token missing from your request".
  PostCallWithErrorResponse,
} from "../api/ApiServices";
import ApiConfig from "../api/ApiConfig";
import { ToastMsg } from "./ToastMsg";
import Loader from "./Loader";

/**
 * Matching Control Center.
 *
 * Everything that decides HOW two members are scored, and how automatic
 * suggestions are generated, in one place. Saving retunes the engine: cached
 * reports carry a config fingerprint, so they recompute themselves on next open.
 *
 * Boundary worth knowing: these settings govern SCORING and SUGGESTIONS. They
 * never override a member's own filter preferences — those belong to the member.
 */
const MatchingControlCenter = () => {
  // Every admin page renders inside the layout shell below; `admin-main` is what
  // offsets the content past the sidebar. Without it the page slides underneath
  // the sidebar and gets clipped.
  const { sidebar } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState(null);

  const load = () => {
    setLoading(true);
    getWithAuthCallWithErrorResponse(ApiConfig.MATCHING_CONFIG)
      .then((res) => {
        if (res?.json?.data) setConfig(res.json.data);
        else ToastMsg("error", res?.json?.message || "Could not load config");
      })
      .catch(() => ToastMsg("error", "Could not load matching config"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const patchFactor = (key, field, value) => {
    setConfig((c) => ({
      ...c,
      factors: c.factors.map((f) =>
        f.key === key ? { ...f, [field]: value } : f
      ),
    }));
  };

  const patchAi = (field, value) =>
    setConfig((c) => ({ ...c, ai: { ...c.ai, [field]: value } }));

  const patchSuggestions = (field, value) =>
    setConfig((c) => ({
      ...c,
      suggestions: { ...c.suggestions, [field]: value },
    }));

  const save = () => {
    setSaving(true);
    PostCallWithErrorResponse(
      ApiConfig.MATCHING_CONFIG,
      JSON.stringify({
        factors: config.factors,
        ai: config.ai,
        suggestions: config.suggestions,
      })
    )
      .then((res) => {
        if (res?.json?.error) {
          ToastMsg("error", res.json.message || "Save failed");
        } else {
          setConfig(res.json.data);
          ToastMsg(
            "success",
            "Matching engine retuned. Existing reports will recompute."
          );
        }
      })
      .catch(() => ToastMsg("error", "Save failed"))
      .finally(() => setSaving(false));
  };

  const resetDefaults = () => {
    if (
      !window.confirm(
        "Reset every factor weight, dealbreaker and AI setting back to the recommended defaults?"
      )
    )
      return;
    setSaving(true);
    PostCallWithErrorResponse(ApiConfig.MATCHING_CONFIG_RESET, "{}")
      .then((res) => {
        if (res?.json?.data) {
          setConfig(res.json.data);
          ToastMsg("success", "Reset to recommended defaults");
        }
      })
      .catch(() => ToastMsg("error", "Reset failed"))
      .finally(() => setSaving(false));
  };

  const runNow = () => {
    setRunning(true);
    PostCallWithErrorResponse(ApiConfig.MATCHING_SUGGESTIONS_RUN, "{}")
      .then((res) => {
        if (res?.json?.error) ToastMsg("error", res.json.message);
        else ToastMsg("success", res?.json?.message || "Suggestions generated");
        load();
      })
      .catch(() => ToastMsg("error", "Could not run suggestions"))
      .finally(() => setRunning(false));
  };

  if (loading || !config) {
    return (
      <main
        id="cx-main"
        className={sidebar ? "admin-main" : "cx-active admin-main"}
      >
        <Loader />
      </main>
    );
  }

  return (
    <main
      id="cx-main"
      className={sidebar ? "admin-main" : "cx-active admin-main"}
    >
      <div className="heading-btn-main">
        <div className="heading-common">
          <label htmlFor="">Matching Engine</label>
        </div>

        <div className="add-btn d-flex">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={resetDefaults}
            disabled={saving}
          >
            Reset to recommended
          </button>
          <button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <p className="text-muted small">
        These settings control how members are <strong>scored</strong> and how
        automatic suggestions are chosen. They never override a member's own
        filter preferences — those belong to the member.
      </p>

      {/* ---------------- Factors ---------------- */}
      <div className="card mb-4">
        <div className="card-header">
          <strong>Compatibility factors</strong>
          <div className="text-muted small">
            Weight decides how much a factor moves the score (0 = it doesn't
            count). A <strong>dealbreaker</strong> means a mismatch drops the
            score to 0 and bars the pair from suggestions.
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Factor</th>
                <th style={{ width: 90 }}>Counts</th>
                <th style={{ width: 260 }}>Weight</th>
                <th style={{ width: 120 }}>Dealbreaker</th>
                <th style={{ width: 110 }}>Comparison</th>
              </tr>
            </thead>
            <tbody>
              {config.factors.map((f) => (
                <tr key={f.key} className={f.enabled ? "" : "text-muted"}>
                  <td>{f.label || f.key}</td>
                  <td>
                    <Form.Check
                      type="switch"
                      checked={f.enabled !== false}
                      onChange={(e) =>
                        patchFactor(f.key, "enabled", e.target.checked)
                      }
                    />
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <input
                        type="range"
                        className="form-range me-2"
                        min="0"
                        max="10"
                        step="1"
                        value={f.weight ?? 5}
                        disabled={f.enabled === false}
                        onChange={(e) =>
                          patchFactor(f.key, "weight", Number(e.target.value))
                        }
                      />
                      <span className="badge bg-light text-dark">
                        {f.weight ?? 5}
                      </span>
                    </div>
                  </td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={Boolean(f.dealbreaker)}
                      disabled={f.enabled === false}
                      onChange={(e) =>
                        patchFactor(f.key, "dealbreaker", e.target.checked)
                      }
                    />
                  </td>
                  <td>
                    <span className="badge bg-secondary">{f.compare}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------------- AI ---------------- */}
      <div className="card mb-4">
        <div className="card-header">
          <strong>AI</strong>
          <div className="text-muted small">
            The score itself is always computed in code. The AI refines the
            nuanced factors and writes the explanation members read.
          </div>
        </div>
        <div className="card-body">
          <Form.Check
            type="switch"
            className="mb-2"
            label="Use AI to refine scores and write explanations"
            checked={config.ai?.enabled !== false}
            onChange={(e) => patchAi("enabled", e.target.checked)}
          />
          <Form.Check
            type="switch"
            className="mb-2"
            label="Generate a report automatically when a member likes someone"
            checked={Boolean(config.ai?.autoReportOnLike)}
            onChange={(e) => patchAi("autoReportOnLike", e.target.checked)}
          />
          <Form.Check
            type="switch"
            className="mb-3"
            label="Generate reports automatically when two members match (both sides)"
            checked={Boolean(config.ai?.autoReportOnMatch)}
            onChange={(e) => patchAi("autoReportOnMatch", e.target.checked)}
          />

          <Form.Label className="fw-bold">Matching instructions</Form.Label>
          <div className="text-muted small mb-1">
            The system prompt the matchmaker follows when judging a pair.
          </div>
          <Form.Control
            as="textarea"
            rows={10}
            value={config.ai?.instructions || ""}
            onChange={(e) => patchAi("instructions", e.target.value)}
          />
        </div>
      </div>

      {/* ---------------- Suggestions ---------------- */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div>
            <strong>Automatic profile suggestions</strong>
            <div className="text-muted small">
              The engine ranks candidates and sends the best ones to each member
              — no manual picking. Suggestions arrive exactly as before (in the
              member's admin chat, with a push notification).
            </div>
          </div>
          <button
            className="btn btn-outline-primary"
            onClick={runNow}
            disabled={running}
          >
            {running ? "Running…" : "Run now"}
          </button>
        </div>
        <div className="card-body">
          <Form.Check
            type="switch"
            className="mb-3"
            label="Send automatic suggestions"
            checked={config.suggestions?.enabled !== false}
            onChange={(e) => patchSuggestions("enabled", e.target.checked)}
          />

          <div className="row">
            <div className="col-md-4 mb-3">
              <Form.Label>Suggestions per member</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="50"
                value={config.suggestions?.perUser ?? 5}
                onChange={(e) =>
                  patchSuggestions("perUser", Number(e.target.value))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <Form.Label>Minimum score to suggest (%)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                value={config.suggestions?.minScore ?? 50}
                onChange={(e) =>
                  patchSuggestions("minScore", Number(e.target.value))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <Form.Label>Schedule (cron)</Form.Label>
              <Form.Control
                type="text"
                value={config.suggestions?.cadence || "0 9 * * *"}
                onChange={(e) => patchSuggestions("cadence", e.target.value)}
              />
              <div className="form-text">
                Default <code>0 9 * * *</code> = every day at 09:00.
              </div>
            </div>
          </div>

          {config.suggestions?.lastRunAt && (
            <div className="alert alert-light border mb-0 py-2 small">
              <strong>Last run:</strong>{" "}
              {new Date(config.suggestions.lastRunAt).toLocaleString()} —{" "}
              {config.suggestions.lastRunSummary}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default MatchingControlCenter;
